import React from 'react'
import { Box, CircularProgress } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Txt from './Txt'

export type SaveState = 'idle' | 'saving' | 'saved'

const sxRow = {
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: '0.35rem',
} as const

// Rückmeldung zum Speichern einer Angabe, direkt am Feld statt in einer Snackbar.
const SaveStateIndicator = React.memo(({ state }: { state: SaveState }): JSX.Element | null =>
	state === 'saving' ? (
		<Box sx={sxRow}>
			<CircularProgress size={14} />
			<Txt variant="caption" color="text.secondary">
				Speichern…
			</Txt>
		</Box>
	) : state === 'saved' ? (
		<Box sx={sxRow}>
			<CheckCircleIcon fontSize="small" color="success" />
			<Txt variant="caption" color="success.main">
				Gespeichert
			</Txt>
		</Box>
	) : null
)

SaveStateIndicator.displayName = 'SaveStateIndicator'

// So lange nach dem letzten Tastendruck wird gespeichert.
const AUTO_SAVE_DELAY_MS = 600

// Speichert values kurz nach jeder Änderung; der Stand beim Öffnen gilt als gespeichert. Scheitert
// save, bleibt der Stand ungespeichert und geht mit der nächsten Änderung erneut raus.
export const useAutoSave = <T,>(values: T, save: (values: T) => Promise<boolean>): SaveState => {
	const [saveState, setSaveState] = React.useState<SaveState>('idle')
	const savedValues = React.useRef(values)

	React.useEffect(() => {
		const timeout =
			values === savedValues.current
				? undefined
				: setTimeout(async () => {
						setSaveState('saving')
						const saved = await save(values)
						if (saved) savedValues.current = values
						setSaveState(saved ? 'saved' : 'idle')
				  }, AUTO_SAVE_DELAY_MS)
		return () => clearTimeout(timeout)
	}, [values, save])

	return saveState
}

export default SaveStateIndicator
